/**
 * BudgetLedgerPage — J7-W2 task 5 (PRD §12.3.10).
 *
 * Per-operator + per-contract LLM spend rollup. Backed by the new
 * `llm_usage_logs.actor_id` column (migration 331) and the operator's role
 * contract `tool_access_policy.budget_cap_usd`.
 *
 * Surfaces:
 *   - Top header: aggregate this-month spend, lockout count, time-series
 *   - Operator cards: spend bar (spend/cap), lockout badge, recent calls
 *   - Per-contract roll-up (group by contractName)
 */

import React, { useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Wallet, AlertTriangle, Clock } from 'lucide-react';
import { RorosPage } from '@/components/roros/RorosPage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { QueryPage } from '@/components/QueryPage';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { t } from '@/lib/i18n';
import { useRorosBudgetLedger } from '@/hooks/roros/governance';
import type { BudgetLedgerOperatorDTO } from '@/services/roros/governance.service';
import { formatDateTimeStr } from '@/lib/localeFormatters';

function fmtUsd(n: number | null): string {
  if (n === null || n === undefined) return '—';
  return `$${n.toFixed(2)}`;
}

function SpendBar({ spend, cap }: { spend: number; cap: number | null }) {
  if (cap === null) {
    return (
      <div className="text-xs text-muted-foreground">
        {t('No cap configured')} — {fmtUsd(spend)} {t('this month')}
      </div>
    );
  }
  const pct = Math.min(100, (spend / cap) * 100);
  const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span>{fmtUsd(spend)}</span>
        <span className="text-muted-foreground">{fmtUsd(cap)}</span>
      </div>
      <div className="h-2 w-full bg-muted rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} data-testid="budget-spend-bar-fill" />
      </div>
    </div>
  );
}

function OperatorCard({ op }: { op: BudgetLedgerOperatorDTO }) {
  return (
    <Card data-testid="budget-operator-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{op.actorName}</CardTitle>
            {op.contractName && (
              <p className="text-xs text-muted-foreground">{op.contractName}</p>
            )}
          </div>
          {op.isLockedOut && (
            <Badge variant="error" className="flex items-center gap-1" data-testid="budget-lockout-badge">
              <AlertTriangle className="h-3 w-3" />
              {t('Locked out')}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <SpendBar spend={op.spendUsd} cap={op.capUsd} />
        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {op.callCount} {t('calls this month')}
        </div>
      </CardContent>
    </Card>
  );
}

export function BudgetLedgerPage() {
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const ledgerQuery = useRorosBudgetLedger({
    actorId: selectedOperator !== 'all' ? selectedOperator : null,
    days: 30,
  });

  const operators = ledgerQuery.data?.currentMonth.operators ?? [];

  const operatorOptions = useMemo(() => {
    return [
      { value: 'all', label: t('All operators') },
      ...operators.map((o) => ({ value: o.actorId, label: o.actorName })),
    ];
  }, [operators]);

  // Group operators by contract for the per-contract section.
  const byContract = useMemo(() => {
    const map = new Map<string, { name: string; ops: BudgetLedgerOperatorDTO[] }>();
    for (const op of operators) {
      const key = op.contractId ?? '__none__';
      const name = op.contractName ?? t('No active contract');
      if (!map.has(key)) map.set(key, { name, ops: [] });
      map.get(key)!.ops.push(op);
    }
    return Array.from(map.values());
  }, [operators]);

  const lockoutCount = operators.filter((o) => o.isLockedOut).length;

  return (
    <RorosPage
      title={t('Budget Ledger')}
      icon={<Wallet className="h-5 w-5" />}
    >
      <QueryPage query={ledgerQuery} emptyMessage={t('Budget ledger unavailable')}>
        {(data) => (
          <div className="space-y-6 mt-6">
            {/* ─── Header ──────────────────────────────────────────── */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('This month spend')}</p>
                    <p className="text-2xl font-semibold" data-testid="budget-month-total">
                      {fmtUsd(data.currentMonth.spendUsd)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('Operators with caps')}</p>
                    <p className="text-2xl font-semibold">
                      {operators.filter((o) => o.capUsd !== null).length}/{operators.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('Locked out (cap reached)')}</p>
                    <p className={`text-2xl font-semibold ${lockoutCount > 0 ? 'text-red-600' : ''}`}>
                      {lockoutCount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ─── Time series ─────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle>{t('Spend — last 30 days')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  label={t('Filter by operator')}
                  value={selectedOperator}
                  onValueChange={setSelectedOperator}
                  options={operatorOptions}
                />
                {data.lastNDays.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    {t('No spend recorded in the last 30 days.')}
                  </p>
                ) : (
                  <div className="h-64 mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.lastNDays}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip formatter={(v: any) => `$${Number(v ?? 0).toFixed(4)}`} />
                        <Area type="monotone" dataKey="spendUsd" stroke="rgb(var(--color-primary-500))" fill="rgb(var(--color-primary-300))" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ─── Per-operator section ────────────────────────────── */}
            <div>
              <h3 className="text-lg font-medium mb-3">{t('Per-operator spend')}</h3>
              {operators.length === 0 ? (
                <Alert>
                  {t('No AI Operators yet. Create one in AI Operators to see ledger data.')}
                </Alert>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {operators.map((op) => (
                    <OperatorCard key={op.actorId} op={op} />
                  ))}
                </div>
              )}
            </div>

            {/* ─── Per-contract section ────────────────────────────── */}
            {byContract.length > 0 && (
              <div>
                <h3 className="text-lg font-medium mb-3">{t('Per-contract roll-up')}</h3>
                <div className="space-y-3">
                  {byContract.map((c, i) => (
                    <Card key={i} data-testid="budget-contract-card">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{c.name}</p>
                          <Badge variant="outline">
                            {c.ops.length} {t('operators')}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t('Total this month')}: <span className="font-medium">{fmtUsd(c.ops.reduce((s, o) => s + o.spendUsd, 0))}</span>
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* ─── Recent calls ────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle>{t('Recent AI calls')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.currentMonth.calls.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">
                    {t('No AI calls recorded yet this month.')}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="budget-recent-calls-table">
                      <thead>
                        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                          <th className="py-2 pr-4">{t('Time')}</th>
                          <th className="py-2 pr-4">{t('Purpose')}</th>
                          <th className="py-2 pr-4">{t('Model')}</th>
                          <th className="py-2 pr-4 text-right">{t('Cost')}</th>
                          <th className="py-2 pr-4">{t('Status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.currentMonth.calls.map((c) => (
                          <tr key={c.id} className="border-b last:border-0">
                            <td className="py-2 pr-4 text-xs text-muted-foreground">
                              {formatDateTimeStr(c.createdAt)}
                            </td>
                            <td className="py-2 pr-4">{c.purpose}</td>
                            <td className="py-2 pr-4 text-xs">{c.model}</td>
                            <td className="py-2 pr-4 text-right">{fmtUsd(c.totalCostUsd)}</td>
                            <td className="py-2 pr-4">
                              <Badge variant={c.status === 'success' ? 'success' : c.status === 'error' ? 'error' : 'secondary'}>
                                {c.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </QueryPage>
    </RorosPage>
  );
}
